import { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fechaInicioMes, fechaFinMes, hoyEnChile } from '../utils/dateHelpers';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  TrendingUp,
  TrendingDown,
  Filter,
  X,
  Download,
  RefreshCw,
  CheckCircle
} from 'lucide-react';
import { useLumisial } from '../context/LumisialContext';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/formatters';
import { puedeOperarDineroDeLumisial } from '../utils/permissions';
import { movimientoService, MovimientoFilters, PaginatedResponse } from '../services/movimientoService';
import { Select } from '../components/ui/Select';
import { supabase, Movimiento } from '../lib/supabase';
import { FormErrorAlert } from '../components/ui/FormErrorAlert';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { VolverButton } from '../components/ui/VolverButton';
import { leerPeriodo, rutaConPeriodo } from '../utils/periodoNavegacion';
import { useRegularizacion } from '../context/RegularizacionContext';
import { ComprobantesPendientesPanel } from '../components/comprobantes/ComprobantesPendientesPanel';
import { ComprobantesDeMovimiento } from '../components/comprobantes/ComprobantesDeMovimiento';
import { MontoCorregido } from '../components/comprobantes/MontoCorregido';
import { AdjuntarComprobanteModal, type ItemParaAdjuntar } from '../components/comprobantes/AdjuntarComprobanteModal';
import { ReembolsoModal } from '../components/comprobantes/ReembolsoModal';
import type { EntidadDinero, Respaldo } from '../types/comprobante';

const CLASS_MONTO_INGRESO = 'text-emerald-600';
const CLASS_MONTO_EGRESO = 'text-red-600';
const CLASS_MONTO_ANULADO = 'text-slate-400';

function getMontoColorClass(mov: Movimiento): string {
  if (mov.anulado) return CLASS_MONTO_ANULADO;
  return mov.tipo === 'ingreso' ? CLASS_MONTO_INGRESO : CLASS_MONTO_EGRESO;
}

function getOrigenBadgeClass(mov: Movimiento): string {
  const esCuotaIglesia = mov.categoria_aporte === 'cuota_iglesia';
  const esCuotaPA = esCuotaIglesia && mov.modo_pago_cuota_iglesia != null;
  if (esCuotaIglesia) return esCuotaPA ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700';
  if (mov.origen_tipo === 'miembro') return 'bg-blue-100 text-blue-700';
  if (mov.origen_tipo === 'actividad') return 'bg-orange-100 text-orange-700';
  if (mov.origen_tipo === 'campaña') return 'bg-pink-100 text-pink-700';
  if (mov.origen_tipo === 'externo') return 'bg-violet-100 text-violet-700';
  if (mov.origen_tipo === 'financiamiento') return 'bg-teal-100 text-teal-700';
  return 'bg-slate-100 text-slate-600';
}

function getOrigenLabel(mov: Movimiento): string {
  const esCuotaIglesia = mov.categoria_aporte === 'cuota_iglesia';
  const esCuotaPA = esCuotaIglesia && mov.modo_pago_cuota_iglesia != null;
  if (esCuotaIglesia) return esCuotaPA ? 'Cuota iglesia · PA' : 'Cuota iglesia · JAL';
  if (mov.origen_tipo === 'financiamiento') return 'Financiamiento';
  return mov.origen_tipo || 'N/A';
}

export const MovimientosPage: React.FC = () => {
  const { selectedLumisial } = useLumisial();
  const selectedLumisialId = selectedLumisial?.id ?? null;
  const selectedLumisialIdRef = useRef(selectedLumisialId);
  const mountedRef = useRef(false);
  const movimientosRequestRef = useRef(0);
  const pendientesRequestRef = useRef(0);
  const { usuario } = useAuth();
  // Registrar, editar y anular dinero de este Lumisial: sus juntas locales y la excepción técnica global.
  const puedeOperar = puedeOperarDineroDeLumisial(usuario, selectedLumisial);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [response, setResponse] = useState<PaginatedResponse>({
    data: [],
    total: 0,
    page: 1,
    pageSize: 20,
    totalPages: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [pendientesReembolso, setPendientesReembolso] = useState<Movimiento[]>([]);
  const [loadingPendientes, setLoadingPendientes] = useState(true);
  const { recargar: recargarRegularizacion } = useRegularizacion();
  const [ahora, setAhora] = useState(() => new Date());
  const [paraAdjuntar, setParaAdjuntar] = useState<{ items: readonly ItemParaAdjuntar[]; modo: 'adjuntar' | 'reemplazar' } | null>(null);
  const [paraReembolsar, setParaReembolsar] = useState<Movimiento | null>(null);
  const entidad: EntidadDinero | null = selectedLumisial
    ? { tipo: 'lumisial', id: selectedLumisial.id, nombre: selectedLumisial.nombre } : null;

  const [busqueda, setBusqueda] = useState('');
  const [tipo, setTipo] = useState<'ingreso' | 'egreso' | ''>('');
  const [origenTipo, setOrigenTipo] = useState<'miembro' | 'externo' | 'actividad' | 'campaña' | 'financiamiento' | ''>('');
  // Período heredado de la tarjeta de Balance. Sin esto, la tarjeta anuncia "N movimientos" del
  // período filtrado y esta pantalla abre el historial completo: promete un número y entrega otro.
  // Con mes=0 el período es anual, igual que en Balance.
  const periodoHeredado = useMemo(() => {
    const p = leerPeriodo(searchParams);
    if (!p) return null;
    if (p.mes === 0) return { inicio: `${p.año}-01-01`, fin: `${p.año}-12-31` };
    return { inicio: fechaInicioMes(p.mes, p.año), fin: fechaFinMes(p.mes, p.año) };
  }, [searchParams]);

  const [fechaInicio, setFechaInicio] = useState(periodoHeredado?.inicio ?? '');
  const [fechaFin, setFechaFin] = useState(periodoHeredado?.fin ?? '');
  const [montoMinimo, setMontoMinimo] = useState('');
  const [montoMaximo, setMontoMaximo] = useState('');
  const [mostrarAnulados, setMostrarAnulados] = useState(false);

  useLayoutEffect(() => {
    selectedLumisialIdRef.current = selectedLumisialId;
  }, [selectedLumisialId]);

  const loadPendientesReembolso = async () => {
    if (!selectedLumisialId || !mountedRef.current || selectedLumisialIdRef.current !== selectedLumisialId) return;
    const requestId = ++pendientesRequestRef.current;
    const isCurrentRequest = () => mountedRef.current
      && selectedLumisialIdRef.current === selectedLumisialId
      && pendientesRequestRef.current === requestId;
    try {
      setLoadingPendientes(true);
      const { data } = await supabase
        .from('movimientos')
        .select('*')
        .eq('lumisial_id', selectedLumisialId)
        .eq('requiere_reembolso', true)
        .eq('reembolsado', false)
        .eq('anulado', false)
        .order('fecha', { ascending: false });
      if (isCurrentRequest()) setPendientesReembolso(data || []);
    } catch (error) {
      if (isCurrentRequest()) reportarErrorInesperado(error, 'No pudimos cargar los reembolsos pendientes.');
    } finally {
      if (isCurrentRequest()) setLoadingPendientes(false);
    }
  };

  const abrirAdjuntar = (mov: Movimiento, respaldo: Respaldo, modo: 'adjuntar' | 'reemplazar') => {
    setParaAdjuntar({
      modo,
      items: [{
        movimientoId: mov.id,
        respaldo,
        descripcion: mov.descripcion ?? null,
        monto: Number(mov.monto),
        fecha: respaldo === 'reembolso' ? mov.reembolso_fecha ?? mov.fecha : mov.fecha,
      }],
    });
  };

  const buildFilters = (): MovimientoFilters => {
    const filters: MovimientoFilters = {};

    if (tipo) filters.tipo = tipo as 'ingreso' | 'egreso';
    if (origenTipo) filters.origenTipo = origenTipo as 'miembro' | 'externo' | 'actividad' | 'campaña' | 'financiamiento';
    if (fechaInicio) filters.fechaInicio = fechaInicio;
    if (fechaFin) filters.fechaFin = fechaFin;
    if (montoMinimo) filters.montoMinimo = parseFloat(montoMinimo);
    if (montoMaximo) filters.montoMaximo = parseFloat(montoMaximo);
    if (busqueda.trim()) filters.busqueda = busqueda.trim();
    if (mostrarAnulados) filters.incluirAnulados = true;

    return filters;
  };

  const loadMovimientos = async (page: number = 1) => {
    if (!selectedLumisialId || !mountedRef.current || selectedLumisialIdRef.current !== selectedLumisialId) return;
    const requestId = ++movimientosRequestRef.current;
    const isCurrentRequest = () => mountedRef.current
      && selectedLumisialIdRef.current === selectedLumisialId
      && movimientosRequestRef.current === requestId;

    try {
      setLoading(true);
      setError(null);
      const filters = buildFilters();
      const data = await movimientoService.getPaginated(
        selectedLumisialId,
        page,
        20,
        filters
      );
      if (isCurrentRequest()) {
        setResponse(data);
        setAhora(new Date());
      }
    } catch (err) {
      if (isCurrentRequest()) {
        setError('No pudimos cargar los movimientos.');
        reportarErrorInesperado(err, 'No pudimos cargar los movimientos.');
      }
    } finally {
      if (isCurrentRequest()) setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      movimientosRequestRef.current += 1;
      pendientesRequestRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (selectedLumisialId) {
      void loadMovimientos(1);
      void loadPendientesReembolso();
    }
    return () => {
      movimientosRequestRef.current += 1;
      pendientesRequestRef.current += 1;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLumisialId]);

  const handleFilterChange = () => {
    loadMovimientos(1);
  };

  const handleGoToOrigen = (movimiento: Movimiento) => {
    if (!movimiento.origen_tipo || !movimiento.origen_id) {
      return;
    }

    switch (movimiento.origen_tipo) {
      case 'miembro':
        // El período de la investigación se conserva al saltar al origen del movimiento.
        navigate(rutaConPeriodo('/lumisial/balance/aportes', searchParams.toString()));
        break;
      case 'actividad': {
        // Si es un egreso (costo), navegar directamente a la pestaña de costos
        const tab = movimiento.tipo === 'egreso' ? 'costos' : undefined;
        const url = tab 
          ? `/lumisial/actividades/${movimiento.origen_id}?tab=${tab}`
          : `/lumisial/actividades/${movimiento.origen_id}`;
        
                
        navigate(url);
        break;
      }
      case 'campaña':
        navigate(`/lumisial/campanas/${movimiento.origen_id}`);
        break;
      default:
        break;
    }
  };

  const isOrigenNavigable = (movimiento: Movimiento): boolean => {
    if (!movimiento.origen_tipo || !movimiento.origen_id) {
      return false;
    }
    return ['miembro', 'actividad', 'campaña'].includes(movimiento.origen_tipo);
  };

  const resetFilters = () => {
    setBusqueda('');
    setTipo('');
    setOrigenTipo('');
    setFechaInicio('');
    setFechaFin('');
    setMontoMinimo('');
    setMontoMaximo('');
    setTimeout(() => loadMovimientos(1), 0);
  };

  const hasActiveFilters = !!(busqueda || tipo || origenTipo || fechaInicio || fechaFin || montoMinimo || montoMaximo);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  const handleExport = () => {
    if (response.data.length === 0) {
      alert('No hay movimientos para exportar');
      return;
    }

    const headers = ['Fecha', 'Tipo', 'Monto', 'Descripción', 'Origen', 'Origen ID'];
    const rows = response.data.map(m => [
      formatDate(m.fecha),
      m.tipo === 'ingreso' ? 'Ingreso' : 'Egreso',
      m.monto,
      m.descripcion || '',
      m.origen_tipo || '',
      m.origen_id || ''
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `movimientos-${hoyEnChile()}.csv`;
    a.click();
  };

  const hayPendientes = pendientesReembolso.length > 0;

  return (
    <div className="space-y-6">
      <VolverButton dataTour="volver-a-balance" onClick={() => { navigate(rutaConPeriodo('/lumisial/balance', searchParams.toString())); }} />
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-3xl font-bold text-slate-800">Movimientos</h2>
            {!loadingPendientes && !hayPendientes && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                <CheckCircle className="w-3.5 h-3.5" />
                Sin reembolsos pendientes
              </span>
            )}
          </div>
          <p className="text-slate-600">Registro completo de ingresos y egresos</p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors"
        >
          <Download className="w-5 h-5" />
          Exportar
        </button>
      </div>

      <FormErrorAlert
        error={error}
        onClear={() => { setError(null); }}
        action={{ label: 'Reintentar', onClick: () => { void loadMovimientos(1); } }}
      />

      {entidad && puedeOperar && (
        <ComprobantesPendientesPanel entidad={entidad} onCambio={() => { void loadMovimientos(response.page); }} />
      )}

      {/* Sección: Pendientes de reembolso */}
      {hayPendientes && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-amber-100 p-2 rounded-lg">
              <RefreshCw className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-amber-900">Pendientes de reembolso</h3>
              <p className="text-sm text-amber-700">{pendientesReembolso.length} {pendientesReembolso.length === 1 ? 'movimiento pendiente' : 'movimientos pendientes'}</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-amber-100 border-b border-amber-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-amber-800">Fecha</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-amber-800">Monto</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-amber-800">Descripción</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-amber-800">Origen</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-amber-800">Ver</th>
                  {puedeOperar && (
                    <th className="px-4 py-3 text-center text-sm font-semibold text-amber-800">Marcar reembolsado</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-200">
                {pendientesReembolso.map((mov) => {
                  const origenBadgeClass = getOrigenBadgeClass(mov);
                  return (
                    <tr key={mov.id} className="hover:bg-amber-100/50 transition-colors">
                      <td className="px-4 py-3 text-sm text-slate-800 font-medium whitespace-nowrap">
                        {formatDate(mov.fecha)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-bold text-red-600">
                          - {formatCurrency(Number(mov.monto))}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">
                        {mov.descripcion || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2.5 py-1.5 rounded-full font-medium ${origenBadgeClass}`}>
                          {mov.origen_tipo || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isOrigenNavigable(mov) ? (
                          <button
                            onClick={() => handleGoToOrigen(mov)}
                            className="inline-flex items-center gap-1 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Ir al origen"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      {puedeOperar && (
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => { setParaReembolsar(mov); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-lg text-xs font-semibold transition-colors"
                          title="Marcar como reembolsado"
                        >
                          <CheckCircle className="w-3.5 h-3.5" />
                          Reembolsado
                        </button>
                      </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por descripción..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyUp={handleFilterChange}
              className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors font-semibold ${
              showFilters || hasActiveFilters
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Filter className="w-5 h-5" />
            Filtros
            {hasActiveFilters && <span className="ml-2 bg-white/30 px-2 py-1 rounded text-xs">Activos</span>}
          </button>
        </div>

        {showFilters && (
          <div className="bg-slate-50 rounded-lg p-6 mb-6 space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Select
                  label="Tipo"
                  options={[
                    { value: 'ingreso', label: 'Ingresos' },
                    { value: 'egreso', label: 'Egresos' },
                  ]}
                  value={tipo}
                  onChange={(v) => { setTipo(v as 'ingreso' | 'egreso' | ''); handleFilterChange(); }}
                  placeholder="Todos"
                />
              </div>

              <div>
                <Select
                  label="Origen"
                  options={[
                    { value: 'miembro', label: 'Miembro' },
                    { value: 'externo', label: 'Externo' },
                    { value: 'actividad', label: 'Actividad' },
                    { value: 'campaña', label: 'Campaña' },
                    { value: 'financiamiento', label: 'Financiamiento' },
                  ]}
                  value={origenTipo}
                  onChange={(v) => { setOrigenTipo(v as 'miembro' | 'externo' | 'actividad' | 'campaña' | 'financiamiento' | ''); handleFilterChange(); }}
                  placeholder="Todos"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Fecha Inicio</label>
                <input
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  onChangeCapture={handleFilterChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Fecha Fin</label>
                <input
                  type="date"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                  onChangeCapture={handleFilterChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Monto Mínimo</label>
                <input
                  type="number"
                  value={montoMinimo}
                  onChange={(e) => setMontoMinimo(e.target.value)}
                  onChangeCapture={handleFilterChange}
                  placeholder="$0"
                  min="0"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Monto Máximo</label>
                <input
                  type="number"
                  value={montoMaximo}
                  onChange={(e) => setMontoMaximo(e.target.value)}
                  onChangeCapture={handleFilterChange}
                  placeholder="$999,999"
                  min="0"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-200">
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800 font-medium text-sm"
                >
                  <X className="w-4 h-4" />
                  Limpiar filtros
                </button>
              )}
              <label className="flex items-center gap-2 ml-auto cursor-pointer text-sm text-slate-500 hover:text-slate-700">
                <input
                  type="checkbox"
                  checked={mostrarAnulados}
                  onChange={(e) => {
                    setMostrarAnulados(e.target.checked);
                    setTimeout(() => loadMovimientos(1), 0);
                  }}
                  className="w-4 h-4 text-slate-600 border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                />
                <span>Mostrar anulados</span>
              </label>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700" />
          </div>
        )}
        {/* Con la carga fallida la lista está vacía porque no se pudo leer, no porque no haya
            nada: el aviso de arriba ya lo explica y decir "no hay movimientos" sería mentir. */}
        {!loading && !error && response.data.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <p className="mb-2">No hay movimientos registrados</p>
            {hasActiveFilters && <p className="text-sm">Intenta cambiar los filtros</p>}
          </div>
        )}
        {!loading && response.data.length > 0 && (
          <>
            {/* Fichas — mobile */}
            <div className="md:hidden space-y-3">
              {response.data.map((mov) => {
                const badgeClass = getOrigenBadgeClass(mov);
                const origenLabel = getOrigenLabel(mov);
                const montoColor = getMontoColorClass(mov);
                const signo = mov.tipo === 'ingreso' ? '+' : '-';
                return (
                  <div
                    key={mov.id}
                    className={`rounded-xl border p-4 ${mov.anulado ? 'bg-slate-100 border-slate-200 opacity-60' : 'bg-white border-slate-200'}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {mov.tipo === 'ingreso'
                          ? <TrendingUp className={`w-4 h-4 flex-shrink-0 ${mov.anulado ? CLASS_MONTO_ANULADO : 'text-emerald-600'}`} />
                          : <TrendingDown className={`w-4 h-4 flex-shrink-0 ${mov.anulado ? CLASS_MONTO_ANULADO : 'text-red-600'}`} />}
                        <span className="text-sm text-slate-500">{formatDate(mov.fecha)}</span>
                        {mov.anulado && (
                          <span className="text-xs px-2 py-0.5 bg-slate-300 text-slate-700 rounded-full font-medium">Anulado</span>
                        )}
                      </div>
                      <span className={`text-sm font-bold whitespace-nowrap ${montoColor} ${mov.anulado ? 'line-through' : ''}`}>
                        {signo} {formatCurrency(Number(mov.monto))}
                        {mov.monto_original != null && <MontoCorregido montoOriginal={Number(mov.monto_original)} />}
                      </span>
                    </div>
                    <p className={`text-sm text-slate-700 mb-1 ${mov.anulado ? 'line-through' : ''}`}>
                      {mov.descripcion || '-'}
                    </p>
                    {mov.anulado && mov.motivo_anulacion && (
                      <p className="text-xs text-slate-400 italic mb-2">{mov.motivo_anulacion}</p>
                    )}
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${badgeClass}`}>
                        {origenLabel}
                      </span>
                      {isOrigenNavigable(mov) && (
                        <button
                          type="button"
                          onClick={() => handleGoToOrigen(mov)}
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Ver origen
                        </button>
                      )}
                    </div>
                    <div className="mt-2">
                      <ComprobantesDeMovimiento movimiento={mov} ahora={ahora} onAdjuntar={puedeOperar ? abrirAdjuntar : undefined} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Tabla — desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Fecha</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Tipo</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Monto</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Descripción</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Origen</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Comprobante</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold text-slate-700">Ver Origen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {response.data.map((mov) => {
                    const montoColorClass = mov.anulado ? `${CLASS_MONTO_ANULADO} line-through` : getMontoColorClass(mov);
                    const origenTipoBadgeClass = getOrigenBadgeClass(mov);
                    const origenLabel = getOrigenLabel(mov);
                    return (
                      <tr key={mov.id} className={`transition-colors ${mov.anulado ? 'bg-slate-100 opacity-60' : 'hover:bg-slate-50'}`}>
                        <td className="px-6 py-4 text-sm text-slate-800 font-medium">
                          <div className="flex items-center gap-2">
                            {formatDate(mov.fecha)}
                            {mov.anulado && (
                              <span className="text-xs px-2 py-0.5 bg-slate-300 text-slate-700 rounded-full font-medium">
                                Anulado
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {mov.tipo === 'ingreso' ? (
                              <>
                                <TrendingUp className={`w-5 h-5 ${mov.anulado ? CLASS_MONTO_ANULADO : 'text-emerald-600'}`} />
                                <span className={`text-sm font-medium ${mov.anulado ? 'text-slate-400 line-through' : 'text-emerald-600'}`}>Ingreso</span>
                              </>
                            ) : (
                              <>
                                <TrendingDown className={`w-5 h-5 ${mov.anulado ? CLASS_MONTO_ANULADO : 'text-red-600'}`} />
                                <span className={`text-sm font-medium ${mov.anulado ? 'text-slate-400 line-through' : 'text-red-600'}`}>Egreso</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-sm font-bold ${montoColorClass}`}>
                            {mov.tipo === 'ingreso' ? '+' : '-'} {formatCurrency(Number(mov.monto))}
                          </span>
                          {mov.monto_original != null && <MontoCorregido montoOriginal={Number(mov.monto_original)} />}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">
                          <div>
                            <span className={mov.anulado ? 'line-through' : ''}>{mov.descripcion || '-'}</span>
                            {mov.anulado && mov.motivo_anulacion && (
                              <p className="text-xs text-slate-400 mt-1 italic">{mov.motivo_anulacion}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-xs px-2.5 py-1.5 rounded-full font-medium ${origenTipoBadgeClass}`}>
                            {origenLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <ComprobantesDeMovimiento movimiento={mov} ahora={ahora} onAdjuntar={puedeOperar ? abrirAdjuntar : undefined} />
                        </td>
                        <td className="px-6 py-4 text-center">
                          {isOrigenNavigable(mov) ? (
                            <button
                              type="button"
                              onClick={() => handleGoToOrigen(mov)}
                              className="inline-flex items-center gap-2 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Ir al origen"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {response.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-200">
            <div className="text-sm text-slate-600">
              Página {response.page} de {response.totalPages} ({response.total} movimientos)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => loadMovimientos(response.page - 1)}
                disabled={response.page === 1}
                className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
                Anterior
              </button>
              <button
                onClick={() => loadMovimientos(response.page + 1)}
                disabled={response.page === response.totalPages}
                className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {paraAdjuntar && entidad && (
        <AdjuntarComprobanteModal
          isOpen
          entidad={entidad}
          items={paraAdjuntar.items}
          modo={paraAdjuntar.modo}
          onClose={() => { setParaAdjuntar(null); }}
          onSuccess={() => { void loadMovimientos(response.page); recargarRegularizacion(); }}
        />
      )}
      {paraReembolsar && entidad && (
        <ReembolsoModal
          isOpen
          entidad={entidad}
          movimiento={paraReembolsar}
          onClose={() => { setParaReembolsar(null); }}
          onSuccess={() => { void loadPendientesReembolso(); void loadMovimientos(response.page); recargarRegularizacion(); }}
        />
      )}
    </div>
  );
};
